import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Container,
  GridLegacy as Grid,
  Card,
  CardContent,
  CardMedia,
  Button,
  Chip,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Switch,
  FormControlLabel,
  Input,
} from '@mui/material';
import {
  ShoppingBag as ShopIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { teamService } from '../../services/teamService';
import { avatarService } from '../../services/avatarService';
import { storageService } from '../../services/storageService';
import { AvatarItem } from '../../types';

interface CreateEditItemDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (item: Partial<AvatarItem>, createRpmAsset: boolean) => Promise<void>;
  editingItem?: AvatarItem | null;
  loading: boolean;
  teamId: string;
}

const CreateEditItemDialog: React.FC<CreateEditItemDialogProps> = ({
  open,
  onClose,
  onSave,
  editingItem,
  loading,
  teamId
}) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: 'hat' as AvatarItem['category'],
    rarity: 'common' as AvatarItem['rarity'],
    price: 0,
    imageUrl: '',
    isActive: true,
  });
  const [createRpmAsset, setCreateRpmAsset] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [imagePreview, setImagePreview] = useState<string | null>(null);

  useEffect(() => {
    if (editingItem) {
      setFormData({
        name: editingItem.name,
        description: editingItem.description,
        category: editingItem.category,
        rarity: editingItem.rarity,
        price: editingItem.price,
        imageUrl: editingItem.imageUrl,
        isActive: editingItem.isActive,
      });
      setImagePreview(editingItem.imageUrl);
      setSelectedFile(null);
    } else {
      setFormData({
        name: '',
        description: '',
        category: 'hat',
        rarity: 'common',
        price: 0,
        imageUrl: '',
        isActive: true,
      });
      setImagePreview(null);
      setSelectedFile(null);
    }
  }, [editingItem, open]);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const validation = storageService.validateImageFile(file);
      if (!validation.valid) {
        alert(validation.error);
        return;
      }
      
      setSelectedFile(file);
      
      // Create preview URL
      const previewUrl = URL.createObjectURL(file);
      setImagePreview(previewUrl);
    }
  };

  const uploadImage = async (): Promise<string> => {
    if (selectedFile) {
      setUploadingImage(true);
      try {
        const downloadURL = await storageService.uploadAvatarItemImage(
          selectedFile,
          teamId,
          formData.name
        );
        setUploadingImage(false);
        return downloadURL;
      } catch (error) {
        setUploadingImage(false);
        throw error;
      }
    }
    return formData.imageUrl; // Return existing URL if no new file selected
  };

  const handleSave = async () => {
    try {
      let imageUrl = formData.imageUrl;
      
      // Upload new image if file is selected
      if (selectedFile) {
        imageUrl = await uploadImage();
      }
      
      await onSave({ ...formData, imageUrl }, createRpmAsset);
      onClose();
    } catch (error) {
      console.error('Error saving item:', error);
      alert('Failed to save item. Please try again.');
    }
  };

  const categories = [
    { value: 'hat', label: 'Hat' },
    { value: 'shirt', label: 'Shirt' },
    { value: 'pants', label: 'Pants' },
    { value: 'shoes', label: 'Shoes' },
    { value: 'accessory', label: 'Accessory' },
    { value: 'background', label: 'Background' },
  ];

  const rarities = [
    { value: 'common', label: 'Common' },
    { value: 'rare', label: 'Rare' },
    { value: 'epic', label: 'Epic' },
    { value: 'legendary', label: 'Legendary' },
  ];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {editingItem ? 'Edit Avatar Item' : 'Create New Avatar Item'}
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={3} sx={{ mt: 1 }}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Item Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Price (Wallet Points)"
              type="number"
              value={formData.price}
              onChange={(e) => setFormData({ ...formData, price: parseInt(e.target.value) || 0 })}
              required
              inputProps={{ min: 0 }}
            />
          </Grid>
          
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Description"
              multiline
              rows={3}
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              required
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Category</InputLabel>
              <Select
                value={formData.category}
                label="Category"
                onChange={(e) => setFormData({ ...formData, category: e.target.value as AvatarItem['category'] })}
              >
                {categories.map((category) => (
                  <MenuItem key={category.value} value={category.value}>
                    {category.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Rarity</InputLabel>
              <Select
                value={formData.rarity}
                label="Rarity"
                onChange={(e) => setFormData({ ...formData, rarity: e.target.value as AvatarItem['rarity'] })}
              >
                {rarities.map((rarity) => (
                  <MenuItem key={rarity.value} value={rarity.value}>
                    {rarity.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12}>
            <Typography variant="subtitle1" gutterBottom>
              Item Image
            </Typography>
            
            {/* Image Preview */}
            {imagePreview && (
              <Box sx={{ mb: 2, textAlign: 'center' }}>
                <img
                  src={imagePreview}
                  alt="Preview"
                  style={{
                    maxWidth: '200px',
                    maxHeight: '200px',
                    objectFit: 'cover',
                    borderRadius: '8px',
                    border: '1px solid #e0e0e0'
                  }}
                />
              </Box>
            )}
            
            {/* File Upload */}
            <Box sx={{ mb: 2 }}>
              <input
                accept="image/*"
                style={{ display: 'none' }}
                id="image-upload"
                type="file"
                onChange={handleFileSelect}
              />
              <label htmlFor="image-upload">
                <Button
                  variant="outlined"
                  component="span"
                  disabled={uploadingImage}
                  startIcon={uploadingImage ? <CircularProgress size={16} /> : <AddIcon />}
                >
                  {uploadingImage ? 'Uploading...' : 'Upload Image'}
                </Button>
              </label>
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                Upload a JPG, PNG, GIF, or WebP image (max 5MB)
              </Typography>
            </Box>
            
            {/* Fallback URL Input */}
            <TextField
              fullWidth
              label="Or enter image URL"
              value={formData.imageUrl}
              onChange={(e) => setFormData({ ...formData, imageUrl: e.target.value })}
              helperText="If you prefer to use an external image URL instead"
              placeholder="https://example.com/image.jpg"
            />
          </Grid>
          
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                />
              }
              label="Available for purchase"
            />
          </Grid>
          
          {!editingItem && (
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={createRpmAsset}
                    onChange={(e) => setCreateRpmAsset(e.target.checked)}
                  />
                }
                label="Create ReadyPlayerMe asset (for 3D avatar integration)"
              />
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button 
          onClick={handleSave} 
          variant="contained" 
          disabled={loading || uploadingImage || !formData.name.trim() || !formData.description.trim() || (!formData.imageUrl.trim() && !selectedFile)}
        >
          {loading || uploadingImage ? 'Saving...' : editingItem ? 'Update' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const CoachAvatarShopPage: React.FC = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [teamId, setTeamId] = useState('');
  const [avatarItems, setAvatarItems] = useState<AvatarItem[]>([]);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<AvatarItem | null>(null);
  const [saving, setSaving] = useState(false);

  const rarities = {
    common: { color: '#8B8B8B', label: 'Common' },
    rare: { color: '#4A90E2', label: 'Rare' },
    epic: { color: '#9C27B0', label: 'Epic' },
    legendary: { color: '#FF9800', label: 'Legendary' },
  };

  useEffect(() => {
    if (user) {
      loadShopData();
    }
  }, [user]);

  const loadShopData = async () => {
    if (!user) return;

    try {
      setLoading(true);
      setError('');

      // Get user's teams (coaches can be head coach or member)
      // First ensure coach/head coach is added as member of their own teams
      await teamService.ensureCoachMembership(user.id);
      
      // Check both coach-owned teams and team memberships
      const [coachTeams, memberTeams] = await Promise.all([
        teamService.getTeamsByCoach(user.id),
        teamService.getTeamsByAthlete(user.id)
      ]);
      
      // Combine and deduplicate teams
      const allTeams = [...coachTeams];
      memberTeams.forEach(memberTeam => {
        if (!allTeams.find(team => team.id === memberTeam.id)) {
          allTeams.push(memberTeam);
        }
      });
      
      const userTeam = allTeams[0] || null;
      if (!userTeam) {
        setError('You are not associated with any team.');
        return;
      }

      setTeamId(userTeam.id);

      // Load avatar items for the team (including inactive ones)
      const items = await avatarService.getAllItems(userTeam.id);
      setAvatarItems(items);

    } catch (err) {
      console.error('Failed to load shop data:', err);
      setError('Failed to load shop. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateItem = async (itemData: Partial<AvatarItem>, createRpmAsset: boolean) => {
    if (!user || !teamId) return;

    try {
      setSaving(true);
      setError('');

      const result = await avatarService.createAvatarItem(
        teamId, 
        user.id, 
        itemData as Omit<AvatarItem, 'id' | 'teamId' | 'coachId' | 'createdAt' | 'updatedAt'>,
        createRpmAsset
      );
      
      if (result.rpmAssetId) {
        setSuccess(`Avatar item created successfully! ReadyPlayerMe asset ID: ${result.rpmAssetId}`);
      } else if (result.error) {
        setSuccess(`Avatar item created successfully! ${result.error}`);
      } else {
        setSuccess('Avatar item created successfully!');
      }
      
      await loadShopData();
    } catch (err) {
      console.error('Failed to create item:', err);
      setError('Failed to create item. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleEditItem = async (itemData: Partial<AvatarItem>) => {
    if (!editingItem) return;

    try {
      setSaving(true);
      setError('');

      await avatarService.updateAvatarItem(editingItem.id, itemData);
      
      setSuccess('Avatar item updated successfully!');
      await loadShopData();
    } catch (err) {
      console.error('Failed to update item:', err);
      setError('Failed to update item. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (item: AvatarItem) => {
    try {
      await avatarService.updateAvatarItem(item.id, { isActive: !item.isActive });
      setSuccess(`Item ${item.isActive ? 'deactivated' : 'activated'} successfully!`);
      await loadShopData();
    } catch (err) {
      console.error('Failed to toggle item status:', err);
      setError('Failed to update item status. Please try again.');
    }
  };

  const handleDeleteItem = async (item: AvatarItem) => {
    if (!window.confirm(`Are you sure you want to delete "${item.name}"? This action cannot be undone.`)) {
      return;
    }

    try {
      await avatarService.deleteAvatarItem(item.id);
      setSuccess('Avatar item deleted successfully!');
      await loadShopData();
    } catch (err) {
      console.error('Failed to delete item:', err);
      setError('Failed to delete item. Please try again.');
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <ShopIcon sx={{ fontSize: 40, color: 'primary.main' }} />
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
              Avatar Shop Management
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Create and manage avatar items for your team
            </Typography>
          </Box>
        </Box>
        
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setCreateDialogOpen(true)}
        >
          Add New Item
        </Button>
      </Box>

      {/* Error/Success Messages */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Avatar Items Grid */}
      <Grid container spacing={3}>
        {avatarItems.length === 0 ? (
          <Grid item xs={12}>
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <ShopIcon sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary">
                No avatar items created yet
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Create your first avatar item to get started!
              </Typography>
            </Box>
          </Grid>
        ) : (
          avatarItems.map((item) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={item.id}>
              <Card 
                sx={{ 
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  opacity: item.isActive ? 1 : 0.6,
                  border: item.isActive ? '1px solid #e0e0e0' : '2px solid #ff9800'
                }}
              >
                <CardMedia
                  component="img"
                  height="200"
                  image={item.imageUrl}
                  alt={item.name}
                  sx={{ objectFit: 'cover' }}
                />
                <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                    <Typography variant="h6" component="div" sx={{ fontWeight: 'bold' }}>
                      {item.name}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <IconButton
                        size="small"
                        onClick={() => handleToggleActive(item)}
                        title={item.isActive ? 'Deactivate' : 'Activate'}
                      >
                        {item.isActive ? <VisibilityIcon /> : <VisibilityOffIcon />}
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => {
                          setEditingItem(item);
                          setEditDialogOpen(true);
                        }}
                        title="Edit"
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteItem(item)}
                        title="Delete"
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </Box>
                  
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2, flexGrow: 1 }}>
                    {item.description}
                  </Typography>
                  
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Chip
                      label={rarities[item.rarity].label}
                      size="small"
                      sx={{ 
                        backgroundColor: rarities[item.rarity].color,
                        color: 'white',
                        fontWeight: 'bold'
                      }}
                    />
                    <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                      {item.price} WP
                    </Typography>
                  </Box>
                  
                  <Chip
                    label={item.isActive ? 'Active' : 'Inactive'}
                    size="small"
                    color={item.isActive ? 'success' : 'warning'}
                    variant={item.isActive ? 'filled' : 'outlined'}
                  />
                </CardContent>
              </Card>
            </Grid>
          ))
        )}
      </Grid>

      {/* Create Item Dialog */}
      <CreateEditItemDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onSave={handleCreateItem}
        loading={saving}
        teamId={teamId}
      />

      {/* Edit Item Dialog */}
      <CreateEditItemDialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        onSave={handleEditItem}
        editingItem={editingItem}
        loading={saving}
        teamId={teamId}
      />
    </Container>
  );
};

export default CoachAvatarShopPage;
